import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Play, BookOpen, Exam, Medal, ArrowLeft, Check, X, Trophy, Rocket, UsersThree, Strategy, Lightning, Key, WarningCircle, SlidersHorizontal, Scales, ShieldCheck, ArrowsDownUp, ChartLineUp, type Icon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { askOrb } from "@/components/orbLive";
import { track } from "@/lib/analytics";
import { celebrate } from "@/lib/celebrate.core";
import { ALL_QUIZZES, ARTICLE_GUIDE, ARTICLE_SUBJECTS, LEARN_ARTICLES, LESSON_QUIZZES, QUIZ_BADGES, QUIZZES, quizScore, isExpert } from "@/lib/learn.core";

const BADGE_ICONS: Record<string, Icon> = { Rocket, UsersThree, Strategy, Lightning, Key, WarningCircle, SlidersHorizontal, Scales, ShieldCheck, ArrowsDownUp, ChartLineUp };
/** The quiz's own badge (QUIZ_BADGES): its colour when earned, grey until then. */
export function QuizBadge({ id, size, earned, className = "" }: { id: string; size: number; earned: boolean; className?: string }) {
  const b = QUIZ_BADGES[id];
  const I = (b && BADGE_ICONS[b.icon]) || Medal;
  return <I size={size} weight={earned ? "fill" : "regular"} className={`shrink-0 ${earned ? "" : "text-muted-foreground"} ${className}`} style={earned && b ? { color: b.color } : undefined} />;
}
import type { QuizState } from "@/lib/engagement.core";
import { useQuizzes, useQuizResult } from "@/hooks/useEngagement";
import { LessonsSection } from "@/components/engagement/Lessons";

/****************
 * LEARN: HUB, ARTICLES AND QUIZZES ON THE WEB. 2026-10-22.
 * Guillaume: Learn opens on Videos, Articles and Quiz, like the AI section;
 * the quizzes test how CopyFi works, a quiz without a mistake earns a badge,
 * and every answer is in the articles. Content: lib/learn.core.ts (twin of the
 * phone's), words: learn.json art.* / quiz.*. Videos stay LearningCenter's.
 ****************/

function useBadges() {
  // Until the records arrive nothing is said about badges: an empty list would read as "none yet".
  const { data, isLoading } = useQuizzes();
  const by: Record<string, QuizState> = {};
  for (const q of data?.quizzes ?? []) by[q.quiz] = q;
  const perfect = Object.values(by).filter((q) => q.perfect_at).map((q) => q.quiz);
  return { by, perfect, expert: isExpert(perfect), loading: isLoading };
}

function Header({ title, subtitle, back }: { title: string; subtitle?: string; back?: { to: string; label: string } }) {
  return (
    <div className="mb-8">
      {back && (
        <Link to={back.to} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
          <ArrowLeft size={14} className="rtl:rotate-180" />{back.label}
        </Link>
      )}
      <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

/** /learn: three doors, the way AI opens on its parts. */
export function LearnHub() {
  const { t } = useTranslation("learn");
  const { perfect, loading } = useBadges();
  const card = (to: string, Icon: typeof Play, title: string, body: string, extra = "") => (
    <>
      <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
        <Icon size={22} className="text-primary" />
      </div>
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground flex-1">{body}</p>
      {extra && <p className="text-xs text-primary">{extra}</p>}
    </>
  );
  const cls = "glass-card rounded-2xl p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors";
  // Each card carries its anchor literally for the Orb's guides (orbGuides.ts, tourTargets.test.ts).
  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto w-full" data-testid="learn-hub">
      <Header title={t("hub.title")} subtitle={t("hub.subtitle")} />
      <div className="grid gap-4 md:grid-cols-3">
        <Link to="/learn/videos" data-tour="learn-videos" className={cls}>{card("/learn/videos", Play, t("hub.videos"), t("hub.videosBody"))}</Link>
        <Link to="/learn/articles" data-tour="learn-articles" className={cls}>{card("/learn/articles", BookOpen, t("hub.articles"), t("hub.articlesBody"))}</Link>
        <Link to="/learn/quiz" data-tour="learn-quiz" className={cls}>
          {card("/learn/quiz", Exam, t("hub.quiz"), t("hub.quizBody"), loading ? "" : t("quiz.expertProgress", { done: perfect.filter((q) => q in QUIZZES).length, total: LEARN_ARTICLES.length }))}
        </Link>
      </div>
    </div>
  );
}

/** /learn/articles: the articles, then the short lessons for the moment. */
export function LearnArticles() {
  const { t } = useTranslation("learn");
  const { by } = useBadges();
  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto w-full" data-testid="learn-articles">
      <Header title={t("hub.articles")} subtitle={t("hub.articlesBody")} back={{ to: "/learn", label: t("hub.title") }} />
      {ARTICLE_SUBJECTS.map((s) => (
        <section key={s.id} className="mb-8">
          <h2 className="text-lg font-semibold mb-3">{t(`subject.${s.id}`)}</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {s.articles.map((id) => (
              <Link key={id} to={`/learn/articles/${id}`} className="glass-card rounded-xl p-4 flex items-center gap-3 hover:border-primary/40 transition-colors">
                <BookOpen size={18} className="text-primary shrink-0" />
                <span className="text-sm font-medium flex-1">{t(`art.${id}.title`)}</span>
                {by[id]?.perfect_at && <span aria-label={t("quiz.badge")}><QuizBadge id={id} size={18} earned /></span>}
              </Link>
            ))}
          </div>
        </section>
      ))}
      <h2 className="text-lg font-semibold mb-3">{t("art.forTheMoment")}</h2>
      <LessonsSection />
    </div>
  );
}

/** /learn/articles/:id */
export function LearnArticle() {
  const { id = "" } = useParams();
  const { t } = useTranslation("learn");
  const a = LEARN_ARTICLES.find((x) => x.id === id);
  if (!a) return <LearnArticles />;
  const title = t(`art.${a.id}.title`);
  return (
    <article className="p-6 lg:p-10 max-w-3xl mx-auto w-full" data-testid="learn-article">
      <Header title={title} back={{ to: "/learn/articles", label: t("art.back") }} />
      {/* The screen this article is about (review #33, 10-24); hidden if missing. */}
      <img src={`${import.meta.env.BASE_URL}learn/${a.id}.jpg`} alt={title}
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        className="mx-auto mb-6 w-full max-w-[340px] rounded-xl border border-white/[0.08] shadow-lg" />
      <div className="space-y-4 text-[15px] leading-relaxed">
        {Array.from({ length: a.paragraphs }, (_, i) => <p key={i} id={`p${i + 1}`}>{t(`art.${a.id}.p${i + 1}`)}</p>)}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-8">
        <Button asChild><Link to={`/learn/quiz/${a.id}`}>{t("art.takeQuiz")}</Link></Button>
        <Button variant="outline" onClick={() => askOrb(`${t("art.askOrb")}: ${title}`, ARTICLE_GUIDE[a.id])}>{t("art.askOrb")}</Button>
      </div>
    </article>
  );
}

/** /learn/quiz: every quiz with its badge, and the expert award. */
export function LearnQuizzes() {
  const { t } = useTranslation("learn");
  const { t: tc } = useTranslation("common");
  const { by, perfect, expert, loading } = useBadges();
  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto w-full" data-testid="learn-quizzes">
      <Header title={t("quiz.title")} subtitle={t("quiz.subtitle")} back={{ to: "/learn", label: t("hub.title") }} />
      <div className={`rounded-2xl p-5 mb-6 flex items-center gap-4 border ${expert ? "border-amber-400/50 bg-amber-400/10" : "border-white/10 bg-white/[0.03]"}`}>
        <Trophy size={32} weight={expert ? "fill" : "regular"} className={expert ? "text-amber-400 shrink-0" : "text-muted-foreground shrink-0"} />
        <div className="min-w-0">
          <p className="font-semibold">{t("quiz.expert")}</p>
          <p className="text-sm text-muted-foreground">{t("quiz.expertBody")}{loading ? "" : ` · ${t("quiz.expertProgress", { done: perfect.filter((q) => q in QUIZZES).length, total: LEARN_ARTICLES.length })}`}</p>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {LEARN_ARTICLES.map((a) => {
          const s = by[a.id];
          return (
            <Link key={a.id} to={`/learn/quiz/${a.id}`} className="glass-card rounded-xl p-4 flex items-center gap-3 hover:border-primary/40 transition-colors">
              <QuizBadge id={a.id} size={22} earned={!!s?.perfect_at} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{t(`art.${a.id}.title`)}</p>
                <p className="text-xs text-muted-foreground">
                  {loading ? "…" : s ? (s.perfect_at ? t("quiz.badge") : t("quiz.best", { score: s.best, total: s.total })) : t("quiz.notYet")}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
      {/* More quizzes: one per short lesson (Guillaume 10-25). */}
      <h2 className="text-lg font-semibold mt-8 mb-3">{t("quiz.lessonsTitle")}</h2>
      <div className="grid gap-3 md:grid-cols-2">
        {Object.entries(LESSON_QUIZZES).map(([qid, lq]) => {
          const s = by[qid];
          return (
            <Link key={qid} to={`/learn/quiz/${qid}`} className="glass-card rounded-xl p-4 flex items-center gap-3 hover:border-primary/40 transition-colors">
              <QuizBadge id={qid} size={22} earned={!!s?.perfect_at} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{tc(`lesson.${lq.lesson}.title`)}</p>
                <p className="text-xs text-muted-foreground">
                  {loading ? "…" : s ? (s.perfect_at ? t("quiz.badge") : t("quiz.best", { score: s.best, total: s.total })) : t("quiz.notYet")}
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/** /learn/quiz/:id: one question at a time; after each answer, the paragraph that answers it. */
export function LearnQuiz() {
  const { id = "" } = useParams();
  const { t } = useTranslation("learn");
  const navigate = useNavigate();
  const save = useQuizResult();
  const quiz = ALL_QUIZZES[id];
  const { t: tc } = useTranslation("common");
  // An article's quiz or a lesson's (LESSON_QUIZZES): its title and the paragraph that answers.
  const lessonOf = LESSON_QUIZZES[id]?.lesson;
  const quizTitle = lessonOf ? tc(`lesson.${lessonOf}.title`) : t(`art.${id}.title`);
  const para = (n: number) => (lessonOf ? tc(`lesson.${lessonOf}.p${n}`) : t(`art.${id}.p${n}`));
  const [answers, setAnswers] = useState<number[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  const [done, setDone] = useState<number | null>(null);
  if (!quiz) return <LearnQuizzes />;
  const n = answers.length;
  const q = quiz[n];
  const restart = () => { setAnswers([]); setPicked(null); setDone(null); };
  const next = () => {
    if (picked === null) return;
    const all = [...answers, picked];
    setAnswers(all); setPicked(null);
    if (all.length === quiz.length) {
      const score = quizScore(all, quiz);
      track("quiz_finished", id, undefined, { score, total: quiz.length });
      setDone(score);
      // Celebrated once: the first time this quiz is done without a mistake.
      save.mutate({ quiz: id, score, total: quiz.length }, {
        onSuccess: (r) => { if (r.first_perfect) celebrate("big"); },
      });
    }
  };
  const back = { to: "/learn/quiz", label: t("quiz.title") };
  if (done !== null) {
    const perfect = done === quiz.length;
    return (
      <div className="p-6 lg:p-10 max-w-2xl mx-auto w-full" data-testid="learn-quiz-result">
        <Header title={quizTitle} back={back} />
        <div className={`glass-card rounded-2xl p-8 text-center space-y-3 ${perfect ? "border-amber-400/50" : ""}`}>
          {perfect ? <QuizBadge id={id} size={56} earned className="mx-auto" /> : <Exam size={48} className="text-primary mx-auto" />}
          <p className="text-2xl font-semibold tabular-nums">{t("quiz.result", { score: done, total: quiz.length })}</p>
          {perfect && <p className="text-amber-300">{t("quiz.perfect")}</p>}
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-3">
            {!perfect && <Button onClick={restart}>{t("quiz.again")}</Button>}
            <Button variant="outline" onClick={() => navigate(`/learn/articles/${id}`)}>{t("quiz.readArticle")}</Button>
            {perfect && <Button variant="outline" onClick={() => navigate("/learn/quiz")}>{t("quiz.title")}</Button>}
          </div>
        </div>
      </div>
    );
  }
  const key = `quiz.${id}.q${n + 1}`;
  return (
    <div className="p-6 lg:p-10 max-w-2xl mx-auto w-full" data-testid="learn-quiz">
      <Header title={quizTitle} back={back} />
      <p className="text-xs text-muted-foreground mb-2">{t("quiz.progress", { n: n + 1, total: quiz.length })}</p>
      <div className="h-1 rounded-full bg-white/10 mb-6 overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${(n / quiz.length) * 100}%` }} />
      </div>
      <h2 className="text-lg font-semibold mb-4">{t(key)}</h2>
      <div className="space-y-2">
        {[0, 1, 2, 3].map((o) => {
          const shown = picked !== null;
          const right = o === q.correct;
          const tone = !shown ? "border-white/10 hover:border-primary/50"
            : right ? "border-success/60 bg-success/10" : o === picked ? "border-danger/60 bg-danger/10" : "border-white/10 opacity-60";
          return (
            <button key={o} type="button" disabled={shown} onClick={() => setPicked(o)}
              className={`w-full text-start rounded-xl border px-4 py-3 text-sm flex items-center gap-3 transition-colors ${tone}`}>
              <span className="flex-1">{t(`${key}.o${o + 1}`)}</span>
              {shown && right && <Check size={16} className="text-success shrink-0" />}
              {shown && o === picked && !right && <X size={16} className="text-danger shrink-0" />}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <div className="mt-5 space-y-3">
          <p className={`text-sm font-medium ${picked === q.correct ? "text-success" : "text-danger"}`}>{picked === q.correct ? t("quiz.right") : t("quiz.wrong")}</p>
          <blockquote className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm leading-relaxed">
            <p className="text-xs text-muted-foreground mb-1">{t("quiz.foundIn")}</p>
            {para(q.answeredIn)}
          </blockquote>
          <Button onClick={next}>{n + 1 === quiz.length ? t("quiz.finish") : t("quiz.next")}</Button>
        </div>
      )}
    </div>
  );
}
